import {AttrsSchema, Component, ConfigOptions} from 'panel';
import debounce from 'lodash/debounce';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {createElem, propagateEvent, rafRetry} from 'common/util/dom';
import {panel2react} from 'common/util/react';

import {
  InputChangeEvent,
  InputSize,
  InputState,
  InputTheme,
  InputType,
  Necessary,
  TextAlign,
  ValidationStatus,
} from './types';
import {template} from './template';
import css from './index.inline.styl';
import './label';

export type InputAttrs = {
  'action-text'?: string;
  'auto-focus'?: boolean;
  'not-focusable'?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  'field-id'?: string;
  'field-name'?: string;
  icon?: string;
  label?: string;
  'show-clear'?: boolean;
  placeholder?: string;
  'pre-text'?: string;
  type?: string;
  size?: string;
  necessary?: string;
  'validation-status'?: string;
  'error-message'?: string;
  maxlength?: number;
  'help-text'?: string;
  'extra-info'?: string;
  min?: string; // use "string" to avoid default value for number type
  max?: string;
  'select-on-focus'?: boolean;
  'text-align'?: TextAlign;
  'hide-focus-styling'?: boolean;
  theme?: string;
  'show-numeric-input-controls'?: boolean;
};

interface InputJsxProps {
  attrs?: InputAttrs;
  props?: {
    value?: string;
  };
  on?: {
    input?: (ev) => void;
    change?: (ev) => void;
    clear?: (ev) => void;
    blur?: (ev) => void;
  };
}

export class InputComponent extends Component<InputState, InputJsxProps['attrs']> {
  static useShadowDom = true;

  static get attrsSchema(): AttrsSchema<InputJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      'auto-focus': {type: `boolean`, description: `If set, automatically focuses on the input field`},
      'not-focusable': `boolean`,
      'hide-focus-styling': {
        type: `boolean`,
        description: `Hide focus styling, typically because a parent is styling the focus state`,
      },
      disabled: {type: `boolean`, description: `Whether input is disabled`},
      readonly: {type: `boolean`, description: `Whether input is readonly`},
      'field-id': {type: `string`, description: `Id of input field, maps to field label for accessibility`},
      'field-name': {type: `string`, description: `Name of the input field, maps to field label`},
      icon: {type: `string`, default: ``, description: `Left icon that goes inline with the input`},
      label: {type: `string`, default: ``, description: `Label on top that describes the input field`},
      'show-clear': {type: `boolean`, description: `Whether allowed to show clear input button`},
      placeholder: {type: `string`, description: `Placeholder text for input`},
      'pre-text': {type: `string`, description: `Mono-width text at the beginning of the input`},
      'action-text': {
        type: `string`,
        description: `Feedback for user to perform action when the input is focused. e.g. "Press enter to search"`,
      },
      type: {type: `string`, enum: Object.values(InputType), default: InputType.Text},
      size: {type: `string`, enum: Object.values(InputSize), default: InputSize.Large},
      necessary: {type: `string`, enum: Object.values(Necessary), default: Necessary.Default},
      'validation-status': {type: `string`, enum: Object.values(ValidationStatus), default: ValidationStatus.Default},
      'error-message': {type: `string`, description: `Error message to show when invalid validation status`},
      maxlength: {type: `number`, description: `Maximum number of characters allowed`, default: Necessary.Optional},
      'help-text': {
        type: `string`,
        description: `If set, an "info" icon will be shown next to the label. This value will be shown as a tooltip on hover`,
      },
      'extra-info': {
        type: `string`,
        description: `If set, we will show this string next to the label. This is useful to give the user additional context such as which SDK verion a field is supported in`,
      },
      min: `string`, // use "string" to avoid default value for number type
      max: `string`,
      'select-on-focus': {
        type: `boolean`,
        description: `Used for fields where user will most likely replace the whole input value instead of editing portions of the input`,
      },
      'text-align': {type: `string`, enum: Object.values(TextAlign)},
      theme: {
        type: `string`,
        enum: Object.values(InputTheme),
        default: InputTheme.Default,
      },
      'show-numeric-input-controls': {
        type: `boolean`,
      },
    };
  }

  searchInput: HTMLInputElement;

  debouncedChange = debounce(() => {
    this.dispatchEvent(new CustomEvent(`change`, {detail: {value: this.state.value}} as InputChangeEvent));
  }, 1000);

  get config(): ConfigOptions<InputState> {
    // @ts-ignore - useShadowDom is a static var
    const useShadowDom = this.constructor.useShadowDom;
    return {
      css: useShadowDom ? css : null,
      template,
      useShadowDom,
      defaultState: {
        value: ``,
      },
      helpers: {
        handleInput: (ev) => {
          this.update({value: ev.target.value});
          propagateEvent(this, ev);
        },
        handleChange: (ev) => {
          ev.stopPropagation();
          const value = ev.target.value;
          this.dispatchEvent(new CustomEvent(`change`, {detail: {value}} as InputChangeEvent));
          // Note: do NOT blur the input here; if a widget needs to blur-on-change, it should be done in the widget itself as a special case to avoid breaking browser-standard behavior by default
        },
        handleBlur: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(`blur`, ev));
        },
        handleClearInput: (ev) => {
          ev.stopPropagation();
          this.update({value: ``});
          this.dispatchEvent(new CustomEvent(`clear`, ev));
          // without this, we focus on mp-input instead of just the input field. focusing on
          // mp-input prevents us from typing our second query, while focusing on the searchInput
          // allows to type something else.
          this.focus();
        },
        handleFocus: (): void => {
          if (this.attr(`select-on-focus`)) {
            this.select();
          }
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.attr(`auto-focus`)) {
      this.focus();
    }
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    //Focus element if auto-focus attribute is added
    if (attr === `auto-focus` && oldVal === null) {
      this.focus();
    }
  }

  get value() {
    return this.state.value;
  }

  set value(value: string) {
    this.update({value});
  }

  focus() {
    rafRetry(() => {
      this.searchInput?.focus();
      if (this.attr(`select-on-focus`)) {
        this.select();
      }
      return Boolean(this.searchInput);
    });
  }

  blur() {
    this.searchInput?.blur();
    return super.blur();
  }

  select() {
    return this.searchInput?.select();
  }

  setElmRef(elm: HTMLInputElement) {
    this.searchInput = elm;
  }
}

class ShadowlessInput extends InputComponent {
  static useShadowDom = false;

  connectedCallback() {
    super.connectedCallback();

    // add mp-input styles as global styles if not already added
    if (!document.head.querySelector(`style#mp-input-css`)) {
      document.head.appendChild(createElem(`style`, {type: `text/css`}, css.toString()));
    }
  }
}

export const ShadowlessInputEl = defineAndMakeJsxFactory<InputJsxProps>(`mp-shadowless-input`, ShadowlessInput);
export const ShadowlessInputReact = panel2react<InputJsxProps>(`mp-shadowless-input`, ShadowlessInput);
export const InputEl = defineAndMakeJsxFactory<InputJsxProps>(`mp-input`, InputComponent);
export const InputReact = panel2react<InputJsxProps>(`mp-input`, InputComponent);
